<script setup>
  import { computed, onUnmounted, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter } from "vue-router";
  import { Plus, Upload } from "lucide-vue-next";

  import { useSurveysStore } from "@/stores/surveys";
  import { useCurrentUserStore } from "@/stores/currentUser";
  import { useSurveysFilters } from "@/utils/composables/filters/useSurveysFilters";
  import SurveysTable from "@/components/Dashboard/SurveysTable.vue";
  import ImportSurveyDataDialog from "@/components/Dashboard/ImportSurveyDataDialog.vue";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { Switch } from "@/components/ui/switch";
  import { StatusTabs, useStatusTabs } from "@/components/episto-ui/status-tabs";
  import { Button } from "@/components/ui/button";
  import zenMan from "@/assets/images/zen_man.png";

  const router = useRouter();
  const surveysStore = useSurveysStore();
  const currentUserStore = useCurrentUserStore();
  const { resetAllFilters, search, clearFilters } = surveysStore;
  const { surveys, surveysCount, currentPage, surveysSearchQuery, surveysStatusFilter, statusCounts, isLoading } =
    storeToRefs(surveysStore);
  const { canCreateSurvey, canImportSurveyData } = storeToRefs(currentUserStore);

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear, freemiumToggle } = useSurveysFilters();

  const isImportDialogOpen = ref(false);

  const navigateToNewSurvey = () => router.push("/surveys/new");

  const STATUS_TABS = [
    { value: "all", label: "All", statuses: [] },
    { value: "live", label: "Live", statuses: ["live"] },
    { value: "draft", label: "Draft", statuses: ["draft"] },
    { value: "complete", label: "Complete", statuses: ["complete"] },
  ];
  const { tabs, activeTab, tabFilterCount } = useStatusTabs(surveysStatusFilter, statusCounts, STATUS_TABS, () => search(1));

  const filtersBadgeCount = computed(() => activeFiltersCount.value - tabFilterCount.value);
  const hasActiveCriteria = computed(() => activeFiltersCount.value > 0 || !!surveysSearchQuery.value);

  onUnmounted(() => resetAllFilters());
</script>

<template>
  <IndexPageLayout
    title="Surveys"
    data-cy="surveys-dashboard-page"
    :action-label="canCreateSurvey ? 'New survey' : undefined"
    @action="navigateToNewSurvey">
    <template #actions>
      <Button
        v-if="canImportSurveyData"
        variant="ghost"
        class="text-muted-foreground"
        @click="isImportDialogOpen = true">
        <Upload />
        Import data
      </Button>
    </template>

    <template #toolbar>
      <StatusTabs
        v-model="activeTab"
        :tabs="tabs" />
      <IndexPageFilters
        v-model:search-model-value="surveysSearchQuery"
        search-placeholder="Search surveys"
        search-class="w-60"
        search-data-cy="dashboard-searchbar"
        class="ml-auto pb-2">
        <template #filters>
          <FilterDropdown
            compact
            :filters="filterConfigs"
            :active-filters-count="filtersBadgeCount"
            :matching-count="surveysCount"
            matching-label="surveys"
            @toggle="handleFilterChange"
            @clear="handleClear">
            <template
              v-if="freemiumToggle.visible"
              #footer-start>
              <label class="flex cursor-pointer items-center gap-2.5 text-13 text-primary">
                <Switch
                  class="data-[state=checked]:bg-ring"
                  :model-value="freemiumToggle.hidden"
                  @update:model-value="freemiumToggle.toggle()" />
                {{ freemiumToggle.label }}
              </label>
            </template>
          </FilterDropdown>
        </template>
      </IndexPageFilters>
    </template>

    <SurveysTable
      :surveys="surveys"
      :surveys-count="surveysCount"
      :current-page="currentPage"
      :loading="isLoading">
      <template #empty>
        <div
          v-if="hasActiveCriteria"
          class="flex flex-col items-center gap-2 py-4">
          <p class="text-13 text-muted-foreground">No study matches these filters.</p>
          <Button
            variant="link"
            size="sm"
            @click="clearFilters">
            Clear filters
          </Button>
        </div>
        <div
          v-else
          class="flex flex-col items-center gap-3 py-6">
          <img
            :src="zenMan"
            alt=""
            class="h-28" />
          <p class="heading-section">No surveys yet</p>
          <Button
            v-if="canCreateSurvey"
            @click="navigateToNewSurvey">
            <Plus />
            New survey
          </Button>
        </div>
      </template>
    </SurveysTable>

    <ImportSurveyDataDialog v-model:open="isImportDialogOpen" />
  </IndexPageLayout>
</template>
